import { useEffect, useMemo, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Settings2, Star } from "lucide-react"
import { type RouteQuadrant } from "../lib/routeMapData"
import { groupFavorites } from "../lib/groupFavorites"
import { useManifestFavorites, PORTAL_FAVORITES_UPDATED } from "../lib/useManifestFavorites"
import { GlitchWord } from "./GlitchWord"

const QUADRANT_DOT: Record<RouteQuadrant, string> = {
  public:   "bg-emerald-400",
  core:     "bg-luster-400",
  admin:    "bg-amber-400",
  creator:  "bg-rose-400",
  redirect: "bg-stone-500",
}
const QUADRANT_ACTIVE_ROW: Record<RouteQuadrant, string> = {
  public: "bg-emerald-500/12 text-emerald-200 border border-emerald-500/30",
  core: "bg-luster-500/12 text-luster-100 border border-luster-500/30",
  admin: "bg-amber-500/12 text-amber-200 border border-amber-500/30",
  creator: "bg-rose-500/12 text-rose-200 border border-rose-500/30",
  redirect: "bg-stone-500/10 text-stone-200 border border-stone-500/30",
}
const QUADRANT_HOVER_ROW: Record<RouteQuadrant, string> = {
  public: "hover:bg-emerald-500/8 hover:text-emerald-100",
  core: "hover:bg-luster-500/8 hover:text-luster-50",
  admin: "hover:bg-amber-500/8 hover:text-amber-100",
  creator: "hover:bg-rose-500/8 hover:text-rose-100",
  redirect: "hover:bg-stone-500/8 hover:text-stone-100",
}

/** Full gradient classes so Tailwind JIT includes them. */
const SEPARATOR_GLOW: Record<RouteQuadrant, string> = {
  core:     "bg-gradient-to-r from-transparent via-luster-400/30 to-transparent",
  admin:    "bg-gradient-to-r from-transparent via-amber-400/30 to-transparent",
  creator:  "bg-gradient-to-r from-transparent via-rose-400/30 to-transparent",
  public:   "bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent",
  redirect: "bg-gradient-to-r from-transparent via-stone-500/20 to-transparent",
}

/**
 * Sidebar-flavored Manifest Mini.
 * No widget chrome — just the link rows, active-path highlighting,
 * and an empty state that nudges the user to set Waypoints in Settings.
 *
 * UI copy: "Waypoints" — DO NOT change to "Pins", "Favorites", or "Favorite Links".
 * Waypoints = the user's quick-nav routes configured in Settings → Waypoints.
 */
export function SidebarManifest({ onNav, tenantCompanyName }: { onNav?: () => void; tenantCompanyName?: string | null }) {
  const { favorites, loading } = useManifestFavorites()
  const location = useLocation()
  const navigate = useNavigate()
  const [glitchTrigger, setGlitchTrigger] = useState(0)

  const groups = useMemo(() => groupFavorites(favorites), [favorites])

  // When favorites are updated from Settings, trigger a short GlitchWord burst so the sidebar feels alive
  useEffect(() => {
    const onUpdated = () => setGlitchTrigger((t) => t + 1)
    window.addEventListener(PORTAL_FAVORITES_UPDATED, onUpdated)
    return () => window.removeEventListener(PORTAL_FAVORITES_UPDATED, onUpdated)
  }, [])

  const isRouteActive = (path: string) => {
    if (path.includes("?")) {
      const [pathBase, pathQuery] = path.split("?")
      if (location.pathname !== pathBase) return false
      const targetParams = new URLSearchParams(pathQuery)
      const currentParams = new URLSearchParams(location.search)
      for (const [k, v] of targetParams.entries()) {
        if (currentParams.get(k) !== v) return false
      }
      return true
    }
    return location.pathname === path
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-3">
        <span className="w-1 h-1 rounded-full bg-white/20 animate-pulse" />
        <span className="font-mono text-[11px] text-white/20">loading…</span>
      </div>
    )
  }

  if (groups.length === 0) {
    return (
      <div className="px-3 py-4 flex flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <Star className="w-3 h-3 text-white/15 shrink-0" />
          <span className="font-mono text-[10px] text-white/20 uppercase tracking-widest">No waypoints</span>
        </div>
        <button
          type="button"
          onClick={() => { navigate("/settings"); onNav?.() }}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] text-white/30 hover:text-violet-400 transition-colors"
        >
          <Settings2 className="w-3 h-3" />
          set waypoints →
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-0.5 min-w-0">
      <div className="flex items-center gap-1.5 px-3 pb-1.5 min-w-0">
        <Star className="w-3 h-3 text-white/20 shrink-0" />
        <span className="font-mono text-[9px] uppercase tracking-widest text-white/25 min-w-0 truncate">
          <GlitchWord word="Waypoints" intensity={2} externalTrigger={glitchTrigger} colorBase="violet" />
        </span>
      </div>
      {groups.map((group) => (
        <div key={group.quadrant}>
          <div className="flex items-center gap-2 px-3 py-1.5">
            <span className="font-mono text-[9px] uppercase tracking-widest text-white/20 shrink-0">
              {group.label}
            </span>
            <div
              className={`h-px flex-1 min-w-0 rounded-full shadow-[0_0_6px_currentColor] ${SEPARATOR_GLOW[group.quadrant]}`}
              style={{ color: "transparent" }}
            />
          </div>
          <ul className="space-y-0.5 list-none">
            {group.routes.map((route) => {
              // /admin and / are both dashboard shells, so route to the concrete
              // users surface to avoid "it did nothing" perception from Waypoints.
              const to = route.path === "/admin" ? "/admin/users?tab=admins" : route.path
              const active = isRouteActive(to)
              const dot = QUADRANT_DOT[route.quadrant]
              const activeClass = QUADRANT_ACTIVE_ROW[route.quadrant]
              const hoverClass = QUADRANT_HOVER_ROW[route.quadrant]
              return (
                <li key={route.key}>
                  <Link
                    to={to}
                    onClick={onNav}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-xl font-mono text-[13px]
                      transition-colors duration-150
                      ${active
                        ? `${activeClass} font-bold`
                        : `text-white/50 ${hoverClass}`
                      }
                    `}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot} ${active ? "opacity-100" : "opacity-50"}`} />
                    {route.path === "/my-trace" && tenantCompanyName ? `${tenantCompanyName} Trace` : route.label}
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </div>
  )
}
